import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import JsBarcode from 'jsbarcode';
import {
  detectarFormatoCodigoBarras,
  esCodigoBarrasValidoParaPreview,
  etiquetaFormatoCodigoBarras,
  jsBarcodeFormat,
  mensajeErrorCodigoBarras,
} from '../../utils/codigoBarras';

export interface CodigoBarrasPreviewProps {
  valor: string;
  descripcion?: string | null;
  compact?: boolean;
  minHeight?: number;
}

const CodigoBarrasPreview: React.FC<CodigoBarrasPreviewProps> = ({
  valor,
  descripcion,
  compact = false,
  minHeight,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const trimmed = (valor || '').trim();
  const formato = detectarFormatoCodigoBarras(trimmed);
  const valido = esCodigoBarrasValidoParaPreview(trimmed);
  const errorMsg = mensajeErrorCodigoBarras(trimmed);
  const altura = compact ? 56 : minHeight ?? 140;

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !valido) return;
    try {
      JsBarcode(svg, trimmed, {
        format: jsBarcodeFormat(formato),
        displayValue: true,
        fontSize: compact ? 12 : 16,
        height: compact ? 40 : Math.max(80, altura - 36),
        margin: compact ? 4 : 10,
        background: '#ffffff',
        lineColor: '#111111',
        width: compact ? 1.4 : 2,
      });
    } catch {
      svg.replaceChildren();
    }
  }, [trimmed, formato, valido, compact, altura]);

  const shellSx = {
    minHeight: compact ? 88 : altura,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    px: compact ? 1 : 2,
    py: compact ? 1 : 2,
    bgcolor: '#fafafa',
    borderRadius: 2,
    border: '1px dashed',
    borderColor: 'divider',
    width: '100%',
    boxSizing: 'border-box' as const,
  };

  if (!trimmed) {
    return (
      <Box sx={shellSx} role="img" aria-label="Sin código de barras">
        <Typography variant="body2" color="text.secondary" align="center">
          El código va a aparecer acá
        </Typography>
      </Box>
    );
  }

  if (!valido || errorMsg) {
    return (
      <Box sx={{ ...shellSx, borderColor: 'warning.main', bgcolor: '#fff8e1' }}>
        <Typography variant="body2" color="warning.dark" align="center">
          {errorMsg || 'No se puede dibujar este código.'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, wordBreak: 'break-all' }}>
          {trimmed}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...shellSx,
        borderStyle: 'solid',
        borderColor: 'divider',
        bgcolor: '#ffffff',
      }}
    >
      <svg
        ref={svgRef}
        role="img"
        aria-label={`Código de barras ${etiquetaFormatoCodigoBarras(formato)} ${trimmed}`}
        style={{ maxWidth: '100%', height: 'auto' }}
      />
      {descripcion ? (
        <Typography
          variant="caption"
          color="text.secondary"
          align="center"
          sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {descripcion}
        </Typography>
      ) : null}
    </Box>
  );
};

export default CodigoBarrasPreview;
